const SKIP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'SVG', 'MATH',
  'CODE', 'PRE', 'TEMPLATE',
]);

const TRANS_NODE_TAG = 'TRANS';

export function isElement(node: Node): node is HTMLElement {
  return node.nodeType === Node.ELEMENT_NODE;
}

export function needTranslate(node: Node): boolean {
  if (!node) return false;

  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent?.replace(/\s/g, '') ?? '';
    if (text.length === 0) return false;
    if ((node as any).translated) return false;
  }

  let el: HTMLElement | null = null;

  if (isElement(node)) {
    el = node;
  } else if (node.parentNode && isElement(node.parentNode)) {
    el = node.parentNode;
  }

  if (!el) return false;
  if (el.isContentEditable) return false;
  if (el.getAttribute('alitranslate') === 'no') return false;
  if (el.closest('[alitranslate="no"]')) return false;
  if (SKIP_TAGS.has(el.tagName)) return false;

  return true;
}

export function getTextNodes(root: Node, excludeSelector?: string): Text[] {
  const nodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();
  while (current) {
    const text = current as Text;
    const content = text.textContent?.replace(/\s/g, '') ?? '';
    const parentEl = text.parentElement;
    if (
      content.length > 0 &&
      text.parentNode &&
      !SKIP_TAGS.has(text.parentNode.nodeName) &&
      text.parentNode.nodeName !== TRANS_NODE_TAG &&
      (!excludeSelector || (parentEl && !parentEl.closest(excludeSelector))) &&
      needTranslate(text)
    ) {
      nodes.push(text);
    }
    current = walker.nextNode();
  }
  return nodes;
}

// --- VisibilityFilter ---

const visibilityCache = new WeakMap<Element, boolean>();

export function isElementVisible(el: Element): boolean {
  const cached = visibilityCache.get(el);
  if (cached !== undefined) return cached;

  let current: Element | null = el;
  while (current) {
    const style = window.getComputedStyle(current);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      cacheSubtreeVisibility(el, false);
      return false;
    }
    current = current.parentElement;
  }

  visibilityCache.set(el, true);
  return true;
}

function cacheSubtreeVisibility(el: Element, visible: boolean): void {
  visibilityCache.set(el, visible);
}

export function clearVisibilityCache(): void {
  // WeakMap entries are auto-collected; this is mainly for testing
}

export function filterVisibleNodes(nodes: Text[]): {
  visible: Text[];
  hidden: Text[];
} {
  const visible: Text[] = [];
  const hidden: Text[] = [];

  for (const node of nodes) {
    const parent = node.parentElement;
    if (!parent) {
      hidden.push(node);
      continue;
    }

    if (isElementVisible(parent)) {
      visible.push(node);
    } else {
      hidden.push(node);
    }
  }

  return { visible, hidden };
}

export function getHiddenAncestors(nodes: Text[]): Map<Element, Text[]> {
  const ancestorMap = new Map<Element, Text[]>();

  for (const node of nodes) {
    let current = node.parentElement;
    let hiddenAncestor: Element | null = null;

    while (current) {
      const style = window.getComputedStyle(current);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
        hiddenAncestor = current;
      }
      current = current.parentElement;
    }

    if (hiddenAncestor) {
      const existing = ancestorMap.get(hiddenAncestor) ?? [];
      existing.push(node);
      ancestorMap.set(hiddenAncestor, existing);
    }
  }

  return ancestorMap;
}

// --- Paragraph Extraction (paragraph_compare) ---

const BLOCK_DISPLAYS = new Set([
  'block', 'flex', 'grid', 'table', 'list-item',
  'table-row', 'table-cell', 'table-caption',
  'flow-root',
]);

export function isBlockElement(el: Element): boolean {
  const display = window.getComputedStyle(el).display;
  return BLOCK_DISPLAYS.has(display);
}

export interface Paragraph {
  flatNodes: Node[];
  rootNodes: Node[];
  commonAncestor: Node;
  text: string;
  display: string;
  isPreWhitespace: boolean;
}

const SKIP_PARAGRAPH_TAGS = new Set([
  'SCRIPT', 'STYLE', 'TEXTAREA', 'SVG', 'NOSCRIPT',
  'MATH', 'CODE', 'PRE', 'TEMPLATE', 'INPUT', 'SELECT',
  TRANS_NODE_TAG,
]);

export function getParagraphs(
  root: Element,
  excludeSelector?: string,
): Paragraph[] {
  const paragraphs: Paragraph[] = [];
  let currentParagraph: Node[] = [];

  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
    {
      acceptNode(node: Node): number {
        if (isElement(node)) {
          if (SKIP_PARAGRAPH_TAGS.has(node.tagName)) return NodeFilter.FILTER_REJECT;
          if (node.getAttribute('alitranslate') === 'no') return NodeFilter.FILTER_REJECT;
          if (excludeSelector && node.matches(excludeSelector)) return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    },
  );

  let current = walker.nextNode();
  while (current) {
    if (isElement(current) && isBlockElement(current)) {
      if (currentParagraph.length > 0) {
        const para = buildParagraph(currentParagraph);
        if (para) paragraphs.push(para);
        currentParagraph = [];
      }
    }

    if (current.nodeType === Node.TEXT_NODE) {
      const text = current.textContent?.replace(/\s/g, '') ?? '';
      if (text.length > 0 && needTranslate(current)) {
        currentParagraph.push(current);
      }
    }

    current = walker.nextNode();
  }

  if (currentParagraph.length > 0) {
    const para = buildParagraph(currentParagraph);
    if (para) paragraphs.push(para);
  }

  return paragraphs;
}

function buildParagraph(nodes: Node[]): Paragraph | null {
  if (nodes.length === 0) return null;

  const text = nodes.map(n => n.textContent ?? '').join('');
  if (text.replace(/\s/g, '').length === 0) return null;

  const ancestor = findCommonAncestor(nodes);
  const rootNodes = findRootNodes(nodes, ancestor);

  const style = ancestor && isElement(ancestor) ? window.getComputedStyle(ancestor) : null;
  const display = style?.display ?? 'block';
  const isPreWhitespace = style?.whiteSpace === 'pre' || style?.whiteSpace === 'pre-wrap';

  return {
    flatNodes: nodes,
    rootNodes,
    commonAncestor: ancestor ?? nodes[0],
    text,
    display,
    isPreWhitespace,
  };
}

function findCommonAncestor(nodes: Node[]): Node | null {
  if (nodes.length === 0) return null;
  if (nodes.length === 1) return nodes[0].parentNode;

  let ancestor: Node | null = nodes[0].parentNode;
  for (let i = 1; i < nodes.length && ancestor; i++) {
    ancestor = findLCA(ancestor, nodes[i]);
  }
  return ancestor;
}

function findLCA(a: Node, b: Node): Node | null {
  const ancestors = new Set<Node>();
  let current: Node | null = a;
  while (current) {
    ancestors.add(current);
    current = current.parentNode;
  }
  current = b;
  while (current) {
    if (ancestors.has(current)) return current;
    current = current.parentNode;
  }
  return null;
}

function findRootNodes(nodes: Node[], ancestor: Node | null): Node[] {
  if (!ancestor) return [...nodes];

  const rootSet = new Set<Node>();
  for (const node of nodes) {
    let current: Node | null = node;
    while (current && current.parentNode !== ancestor) {
      current = current.parentNode;
    }
    if (current) rootSet.add(current);
  }
  return Array.from(rootSet);
}

// --- Translation application ---

export function applyPureTranslation(textNode: Text, translation: string): void {
  (textNode as any).sourceText = (textNode as any).sourceText || textNode.textContent;
  (textNode as any).translateTarget = translation;
  textNode.textContent = translation;
  (textNode as any).translated = true;
}

export function restorePureTranslation(root: Node): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();
  while (current) {
    const text = current as any;
    if (text.sourceText !== undefined) {
      text.textContent = text.sourceText;
      text.translated = false;
    }
    current = walker.nextNode();
  }
}

export function createTransNode(): HTMLElement {
  const trans = document.createElement('trans');
  trans.setAttribute('alitranslate', 'no');
  return trans;
}

export function removeTransNodes(root: Element): void {
  const transNodes = root.querySelectorAll('trans');
  transNodes.forEach(node => node.remove());
}

export function normalizeWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

export function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}
